import React, { useCallback } from "react";
import { showDialogAsync } from "../../dialog-queue";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { BottomPhotoMapsDialog } from "../../bottom-maps-menus/bottom-photo-maps-dialog";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useAppContext } from "../../../hooks/useAppContext";
import { useSelectedSpread } from "../../../hooks/useSelection";
import { useDefinition } from "../../../hooks/useDefinition";
import { useImporter } from "../../../contexts/importer-context";
import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";

function isMapPhotoSupportedOnDefinition(definition: eXpress.core.Definition | null) {
	return !!definition?.hasZoneForPhotoSourceType("map");
}

let hasLoggedMissingConfig = false;

/** Hook that returns state or methods to work with Photo Maps */
export function useMapZones() {
	const { createErrorHandler } = useErrorHandling();
	const dashboard = useDashboardContext();
	const {
		editor,
		platform: { ajax },
		context,
	} = useAppContext();
	const importer = useImporter();

	// TODO: listen to messages for possible changes (add/remove spread)
	const spread = useSelectedSpread();
	const isSupportedOnSpread =
		(!!context.osmTileServerUrl && !!spread?.hasZoneForPhotoSourceType("map")) ?? false;
	const { busyBlocker } = useBusyBlockerContext();

	// TODO: listen to messages for possible changes (add/remove zones)
	const definition = useDefinition();
	const hasMapInDefinition = isMapPhotoSupportedOnDefinition(definition);
	const isSupportedOnDefinition =
		!!context.osmTileServerUrl &&
		!!context.osmMapboxAccessToken &&
		isMapPhotoSupportedOnDefinition(definition);

	if (
		!hasLoggedMissingConfig &&
		hasMapInDefinition &&
		(!context.osmTileServerUrl || !context.osmMapboxAccessToken)
	) {
		hasLoggedMissingConfig = true;
		const error = new eXpress.GeneralException(
			"MapConfigMissingException",
			`Product contains map zones, but is missing configuration:\n ${
				!context.osmTileServerUrl ? "no tile server configured \n" : ""
			} ${!context.osmMapboxAccessToken ? "missing accessToken for address search" : ""}`,
		);
		createErrorHandler()(error);
	}

	const openDialogAsync = useCallback(() => {
		const photoZone = spread
			?.getFillablePhotoZones(false)
			.find((pz) => pz.isZoneForPhotoSourceType("map"));
		if (!photoZone) return Promise.resolve(undefined);

		// TODO: Lazy load the dialog from a separate bundled 'express-maps' file
		return showDialogAsync<eXpress.client.IMapPhotoSourceData | undefined>(
			({ resolve }, dialogProps) => (
				<BottomPhotoMapsDialog
					initialData={
						{
							...photoZone.defaultPhotoSourceConfig,
							...(eXpress.client.isMapPhotoSourceItem(
								photoZone.photo?.volatileImageRef,
							)
								? photoZone.photo?.volatileImageRef
								: undefined),
						} as eXpress.client.IMapPhotoSourceData
					}
					onOk={(data) => resolve(data)}
					onCancel={() => resolve(undefined)}
					{...dialogProps}
				/>
			),
		)
			.then(async (mapPhotoSourceData) => {
				if (!mapPhotoSourceData || !mapPhotoSourceData.zoom) return;

				const mapPhotoSourceItem = await busyBlocker.during(
					eXpress.client.MapPhotoSourceItem.fromRemoteUrlAsync(mapPhotoSourceData, {
						ajax,
					}),
				);

				// Create Photo
				const photo = new eXpress.core.Photo(mapPhotoSourceItem, editor.assets.photos);

				// Replace map photo in zone
				dashboard.applyPhotoToZone(photoZone, photo);

				// Add new photo in PhotoSelection list and upload
				editor.addToPhotoSelection([photo], (photos) => {
					importer?.addToQueue(photos);
				});
			})
			.catch(createErrorHandler());
	}, [spread, createErrorHandler, busyBlocker, ajax, dashboard, editor, importer]);

	return {
		isMapsSupportedOnDefinition: isSupportedOnDefinition,
		/** undefined if not allowed on the current spread */
		openZoneMapAsync: isSupportedOnSpread ? openDialogAsync : undefined,
	};
}
